import { AuthUser } from '../models/IAuth';
import { apiFetch } from './http';
import { isNativePlatform } from './platform';
import { getRefreshTokenSync, setTokens, clearTokens } from './tokenStore';
import { startNativeOAuth } from './nativeOAuth';

// Maps known server-side error strings to user-friendly text. Anything not
// in this map falls through to a sensible default for the given context.
const ERROR_MESSAGE_MAP: Array<{ match: RegExp; message: string }> = [
    { match: /invalid credentials/i, message: 'Incorrect email or password.' },
    { match: /invalid email or password/i, message: 'Incorrect email or password.' },
    { match: /email or phone is required/i, message: 'Please enter your email address.' },
    { match: /invalid request payload/i, message: 'Something went wrong. Please try again.' },
    { match: /account not verified/i, message: 'Your account is not verified yet. Please check your email for a verification link.' },
    { match: /account is locked.*?(\d+) minutes?/i, message: 'Your account is temporarily locked. Please try again in $1 minutes.' },
    { match: /account locked due to multiple failed login attempts.*?(\d+) minutes?/i, message: 'Your account has been locked due to too many failed sign-in attempts. Please try again in $1 minutes.' },
    { match: /email already (registered|in use|exists)/i, message: 'An account with that email already exists.' },
    { match: /password.*too (short|weak)/i, message: 'That password does not meet the minimum requirements.' },
    { match: /too many requests/i, message: 'Too many attempts. Please wait a moment and try again.' },
    { match: /method not allowed/i, message: 'Something went wrong. Please try again.' },
];

function humanizeServerMessage(raw: string): string {
    const trimmed = raw.trim();
    if (!trimmed) return '';
    for (const { match, message } of ERROR_MESSAGE_MAP) {
        const m = trimmed.match(match);
        if (m) {
            return message.replace(/\$(\d+)/g, (_, i) => m[Number(i)] ?? '');
        }
    }
    // Don't surface raw text that looks like a stack trace or JSON blob.
    if (trimmed.length > 200 || trimmed.includes('\n')) return '';
    return trimmed;
}

// Reads an error response body once and returns a user-facing message.
// Handles both JSON ({ error|message }) and plain-text (http.Error) payloads.
async function parseErrorMessage(res: Response, fallback: string): Promise<string> {
    let body = '';
    try {
        body = await res.text();
    } catch {
        return fallback;
    }
    let raw = '';
    if (body) {
        try {
            const data = JSON.parse(body);
            raw = (data && (data.error || data.message)) || '';
        } catch {
            raw = body;
        }
    }
    const friendly = humanizeServerMessage(raw);
    if (friendly) return friendly;
    if (res.status === 429) return 'Too many attempts. Please wait a moment and try again.';
    if (res.status >= 500) return 'The server is having trouble right now. Please try again shortly.';
    return fallback;
}

export async function getCSRFToken(): Promise<string> {
    // Native clients authenticate with Bearer tokens and are exempt from CSRF
    // at the BFF, so there is no token to fetch.
    if (isNativePlatform()) return '';
    const res = await apiFetch('/api/csrf', { method: 'GET' });
    if (!res.ok) throw new Error('Failed to fetch CSRF token');
    const data = await res.json();
    return data.csrf_token;
}

async function postWithCSRF(url: string, body: Record<string, string>): Promise<Response> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    // Web clients must present the double-submit CSRF token; native clients skip it.
    if (!isNativePlatform()) {
        headers['X-CSRF-Token'] = await getCSRFToken();
    }
    return apiFetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
    });
}

// Attempt to refresh the session. Web uses the HttpOnly refresh-token cookie;
// native sends the stored refresh token in the body and persists the rotated
// tokens returned by the BFF. Returns true if the refresh succeeded.
let refreshInProgress: Promise<boolean> | null = null;

async function tryRefreshToken(): Promise<boolean> {
    // Deduplicate concurrent refresh attempts (e.g. multiple 401s at once)
    if (refreshInProgress) return refreshInProgress;

    refreshInProgress = (async () => {
        try {
            if (isNativePlatform()) {
                const refreshToken = getRefreshTokenSync();
                if (!refreshToken) return false;
                const res = await apiFetch('/api/refresh', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ refresh_token: refreshToken }),
                });
                if (!res.ok) return false;
                const data = await res.json();
                if (!data.access_token) return false;
                await setTokens(data.access_token, data.refresh_token);
                return true;
            }
            const res = await apiFetch('/api/refresh', { method: 'POST' });
            return res.ok;
        } catch {
            return false;
        } finally {
            refreshInProgress = null;
        }
    })();

    return refreshInProgress;
}

// Wrapper around apiFetch that automatically retries once on 401 after
// refreshing the access token. Use this for any authenticated API call.
export async function fetchWithAuth(input: string, init?: RequestInit): Promise<Response> {
    let res = await apiFetch(input, init);

    if (res.status === 401) {
        const refreshed = await tryRefreshToken();
        if (refreshed) {
            res = await apiFetch(input, init);
        }
    }

    return res;
}

export const authApi = {
    async getMe(): Promise<AuthUser | null> {
        const res = await fetchWithAuth('/api/me', { method: 'GET' });
        if (!res.ok) return null;
        const data = await res.json();
        return {
            uid: data.uid ?? '',
            displayName: data.name ?? '',
            email: data.email ?? '',
            metadata: { role: data.role },
            photoURL: '',
            role: data.role ?? 'user',
            hasPassword: !!data.has_password,
        };
    },

    async register(email: string, password: string, displayName: string): Promise<{ success: boolean; message: string }> {
        const res = await postWithCSRF('/api/register', { email, password, name: displayName });
        if (!res.ok) {
            throw new Error(await parseErrorMessage(res, 'Registration failed. Please try again.'));
        }
        let data: any = {};
        try { data = await res.json(); } catch { /* server may return empty body */ }
        return { success: true, message: data.message || 'Registration successful' };
    },

    async login(email: string, password: string): Promise<{ user: AuthUser | null; message?: string }> {
        const res = await postWithCSRF('/api/login', { email, password });
        if (!res.ok) {
            throw new Error(await parseErrorMessage(res, 'Sign in failed. Please try again.'));
        }
        let data: any = {};
        try { data = await res.json(); } catch { /* server may return empty body */ }
        // Web: BFF set an HttpOnly session cookie. Native: tokens come back in the
        // body and must be persisted before we can call the authenticated /api/me.
        if (isNativePlatform() && data.access_token) {
            await setTokens(data.access_token, data.refresh_token);
        }
        const user = await authApi.getMe();
        return { user, message: data.message };
    },

    async logout(): Promise<void> {
        if (isNativePlatform()) {
            const refreshToken = getRefreshTokenSync();
            try {
                await apiFetch('/api/logout', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ refresh_token: refreshToken ?? '' }),
                });
            } finally {
                await clearTokens();
            }
            return;
        }
        const csrfToken = await getCSRFToken();
        await apiFetch('/api/logout', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-Token': csrfToken,
            },
        });
    },

    oauthLogin(provider: 'google' | 'facebook') {
        // Native: open the system browser; the flow completes via the
        // crapapp:// deep link handled in AppStateProvider. Web: full-page
        // redirect through the BFF OAuth proxy.
        if (isNativePlatform()) {
            void startNativeOAuth(provider);
            return;
        }
        window.location.href = `/api/oauth/${provider}/login`;
    },

    async requestPasswordReset(email: string): Promise<void> {
        const res = await postWithCSRF('/api/request-password-reset-email', { email });
        if (!res.ok) {
            throw new Error(await parseErrorMessage(res, 'We could not send a reset link. Please try again.'));
        }
    },

    async resetPassword(token: string, newPassword: string): Promise<void> {
        const res = await postWithCSRF('/api/email-reset-password', { token, new_password: newPassword });
        if (!res.ok) {
            throw new Error(await parseErrorMessage(res, 'Could not reset your password. The link may be invalid or expired.'));
        }
    },

    async verifyEmail(token: string): Promise<void> {
        const res = await postWithCSRF('/api/verify-email', { token });
        if (!res.ok) {
            throw new Error(await parseErrorMessage(res, 'Email verification failed. The link may be invalid or expired.'));
        }
    },

    async verifyPhone(token: string): Promise<void> {
        const res = await apiFetch('/api/verify-phone?token=' + encodeURIComponent(token), {
            method: 'GET',
        });
        if (!res.ok) {
            throw new Error(await parseErrorMessage(res, 'Phone verification failed. The link may be invalid or expired.'));
        }
    },

    async updateProfile(name: string): Promise<AuthUser> {
        const csrfToken = await getCSRFToken();
        const res = await fetchWithAuth('/api/me', {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-Token': csrfToken,
            },
            body: JSON.stringify({ name }),
        });
        if (!res.ok) {
            throw new Error(await parseErrorMessage(res, 'Could not update your profile. Please try again.'));
        }
        const data = await res.json();
        return {
            uid: data.uid ?? '',
            displayName: data.name ?? '',
            email: data.email ?? '',
            metadata: { role: data.role },
            photoURL: '',
            role: data.role ?? 'user',
            hasPassword: !!data.has_password,
        };
    },

    async changePassword(currentPassword: string, newPassword: string): Promise<void> {
        const csrfToken = await getCSRFToken();
        const res = await fetchWithAuth('/api/change-password', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-Token': csrfToken,
            },
            body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
        });
        if (!res.ok) {
            throw new Error(await parseErrorMessage(res, 'Could not change your password. Please try again.'));
        }
    },

    async deleteAccount(password?: string): Promise<void> {
        const csrfToken = await getCSRFToken();
        const res = await fetchWithAuth('/api/account', {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-Token': csrfToken,
            },
            body: JSON.stringify({ password: password ?? '' }),
        });
        if (!res.ok) {
            throw new Error(await parseErrorMessage(res, 'Could not delete your account. Please try again.'));
        }
    },
};
